import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import { useKBar } from 'kbar';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../hooks/useStore';
import { SetupGuideSection } from './SetupGuideSection';
import { LatestReleaseSection } from './LatestReleaseSection';
import { useLatestRelease } from '../../hooks/useLatestRelease';
import { useGetAllRecommendedResourcesQuery } from '../../services/api';
import { ResourcesSection } from './ResourcesSection';
import { RouteContext } from '../../context';
import { shortcutsSidebarOpen } from '../../store/shortcuts/slice';
import history from '../../history';
import { toggleHelpCenterModalAction } from '../../store/ui/actions';
import { useGetDismissedLatestReleaseQuery } from '../../services/api/LatestReleaseApi';
import StandardPage from '../UI/StandardPage';
import { CheckIcon } from '../Icons';
import { FTUX_V2_TASKS, TASKS_CODES } from '../../constants';
import { OnBoardingTask } from '../../store/user/types';
import { SETTINGS_PAGE_MAX_WIDTH } from '../../constants/uiConsts';
import { RootState } from '../../store/reduxTypes';
import { DateUtils } from '../../utils';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      paddingTop: 40,
      maxWidth: SETTINGS_PAGE_MAX_WIDTH,
      width: '100%',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      // this should add a vertical spacing between
      //  sections setup guide and latest release
      gap: 40,
      paddingBottom: '100px',
    },
  }),
);

enum ResourceAction {
  openShortcut = 'shortcuts',
  contactSupport = 'contactSupport',
  openCommandbar = 'openCommandbar',
}
interface QueryParams {
  pageAction: ResourceAction;
}
export const HomePage = () => {
  const { query } = React.useContext(RouteContext);
  const { pageAction } = query as QueryParams;
  const onboardingTasks = useAppSelector(state => state.tasks.items);
  const userInstance = useAppSelector(state => state.user.instance);
  const isUserEmailVerified = Boolean(userInstance?.attributes?.email_verified);
  const currentUserCreatedDate = useSelector(
    (state: RootState) =>
      state.users.internalUsers.find(it => it.id === state.user.id)
        ?.createdDate,
  );
  const userIsWeekOld =
    DateUtils.getDaysDiffOfDate(new Date(currentUserCreatedDate as string)) > 7;
  const verifyEmailTask = {
    fields: {
      done: isUserEmailVerified,
      taskCode: TASKS_CODES.VERIFY_EMAIL,
    },
  } as OnBoardingTask;

  const availableTasks = (onboardingTasks || [])
    .filter(task => FTUX_V2_TASKS[task.fields.taskCode])
    .concat(verifyEmailTask)
    .sort((a, b) => Number(a.fields.done) - Number(b.fields.done));

  const latestReleasePost = useLatestRelease(!userIsWeekOld);
  const {
    data: latestReleaseDismissal,
    isLoading: isLoadingDismissedLatestRelease,
  } = useGetDismissedLatestReleaseQuery(null, { skip: !userIsWeekOld });

  const {
    isLoading: isRecommendedResourcesLoading,
    data: recommendedResources,
  } = useGetAllRecommendedResourcesQuery();

  const classes = useStyles();
  const dispatch = useAppDispatch();

  /**
   * This function resets the query search params
   */
  const resetQuerySearch = () => {
    history.replace({
      search: '',
    });
  };

  const { query: commandBarQuery } = useKBar();

  const handleResourceCardActions = () => {
    switch (pageAction) {
      case ResourceAction.openShortcut:
        dispatch(shortcutsSidebarOpen(true));
        break;

      case ResourceAction.contactSupport:
        dispatch(toggleHelpCenterModalAction(true));
        break;

      case ResourceAction.openCommandbar:
        commandBarQuery.toggle();
        break;

      default:
        break;
    }
  };

  React.useEffect(() => {
    handleResourceCardActions();
    // reset pageAction query param after resource card is handled
    if (pageAction) resetQuerySearch();
  }, [pageAction]);

  // this determines whether to show the latest release card or not
  // it actually compares the latest release post id with the latest release dismissal
  // state. If the latest release id is in dismissal object do not show it.
  const showLatestReleaseSection =
    latestReleaseDismissal?.structFields.latest_release_dismissed !==
      latestReleasePost?.id && !isLoadingDismissedLatestRelease;

  const showRecommendedResourcesSection = Boolean(
    recommendedResources &&
      recommendedResources?.structFields?.length &&
      !isRecommendedResourcesLoading,
  );

  const showSetupGuideSection = Boolean(
    availableTasks.filter(task => !task.fields.done).length,
  );

  return (
    <StandardPage
      title={`That's it for now`}
      topIcon={<CheckIcon />}
      desc="Check back later for more insights and advice about your business."
      showContent={Boolean(
        showLatestReleaseSection ||
          showRecommendedResourcesSection ||
          showSetupGuideSection ||
          // do not show empty state when data is still loading
          isRecommendedResourcesLoading ||
          isLoadingDismissedLatestRelease,
      )}
    >
      <div className={classes.root}>
        {showSetupGuideSection && <SetupGuideSection tasks={availableTasks} />}
        {showLatestReleaseSection && (
          <LatestReleaseSection latestReleasePost={latestReleasePost} />
        )}
        {showRecommendedResourcesSection && (
          <ResourcesSection
            resources={recommendedResources?.structFields || []}
          />
        )}
      </div>
    </StandardPage>
  );
};
